import { forwardRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { User } from '../../models/users.models'
import List, { ListModeType, ListProps, ListRef } from '../common/List.common'
import { ColumnItem, ItemType } from '../../models/props.models'
import { Order, getOrderStatusColor } from '../../models/orders.models'
import { PlatformReference, getPlatform } from '../../models/platforms.models'
import useRoute from '../../hooks/useRoute.hooks'
import { Route } from '../../models/commons.models'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { Edit } from '@mui/icons-material'

export interface OrderBuyerListProps extends Omit<ListProps, 'modes'> {
  isClient?: boolean
  adminPlatform?: PlatformReference
  isCatalogPage?: boolean
  onCommentClick?: (order: Order) => void
}

const OrderBuyerList = forwardRef<ListRef, OrderBuyerListProps>((props, ref) => {
  const { t } = useTranslation()
  const { goTo } = useRoute()

  const { adminPlatform, isClient, isCatalogPage, onCommentClick, ...listProps } = props

  const columns = useMemo<ColumnItem[]>(
    () => [
      (order: Order | undefined) => ({
        label: t('orders:attributes.client'),
        key: 'client',
        type: adminPlatform ? ItemType.link : ItemType.typo,
        props: {
          icon:
            order && adminPlatform && order.client.organization.platform !== adminPlatform
              ? getPlatform(order.client.organization.platform).icon
              : undefined,
          onClick: (e: any) => {
            if (order && adminPlatform) {
              goTo({ route: Route.user, userId: order.client._id })
              e.stopPropagation()
            }
          },
        },
        formatValue: (client: User) =>
          `${order?.client.organization.name.toUpperCase()} : ${client.lastname} ${
            client.firstname
          }`,
      }),
      ...(isCatalogPage
        ? []
        : [
            (order: Order | undefined) => ({
              label: t('orders:attributes.catalog'),
              key: 'catalog.name',
              type: adminPlatform ? ItemType.link : ItemType.typo,
              props: {
                icon:
                  order && adminPlatform && order.catalog.organization.platform !== adminPlatform
                    ? getPlatform(order.catalog.organization.platform).icon
                    : undefined,
                onClick: (e: any) => {
                  if (order && adminPlatform) {
                    goTo({
                      route: Route.workspaceCatalog,
                      catalogId: order.catalog._id,
                    })
                    e.stopPropagation()
                  }
                },
                children: order?.catalog.name,
              },
            }),
          ]),
      { label: t('orders:attributes.orderNumber'), key: 'orderNumber' },
      {
        label: t('orders:attributes.createdAt'),
        key: 'createdAt',
        formatValue: (createdAt: string) =>
          t('global:format.date', {
            date: new Date(createdAt),
          }),
      },
      (order: Order | undefined) => ({
        label: t('orders:attributes.status'),
        key: 'status',
        type: ItemType.chip,
        props: {
          full: true,
          color: order ? getOrderStatusColor(order?.status) : '',
          label: order ? t(`orders:status.${order?.status}`) : '',
        },
      }),
      (order: Order | undefined) => ({
        label: t('orders:attributes.comment'),
        key: 'comment',
        type: ItemType.custom,
        custom: (
          <Box display="flex" alignItems="center">
            <Typography
              flexGrow={1}
              sx={{
                color: order?.comment ? 'text.primary' : 'text.secondary',
              }}>
              {order?.comment
                ? order.comment.length > 50
                  ? `${order.comment.substring(0, 50)}...`
                  : order.comment
                : t('orders:components.modalComment.addTitle')}
            </Typography>
            <Tooltip
              title={
                order?.comment
                  ? t('orders:components.modalComment.modifyTitle')
                  : t('orders:components.modalComment.addTitle')
              }>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  if (order) onCommentClick?.(order)
                }}>
                <Edit fontSize="small" sx={{ color: 'primary.main' }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      }),
    ],
    [adminPlatform, t, goTo, isCatalogPage, onCommentClick],
  )

  const subColumns = useMemo<ColumnItem[]>(
    () => [
      { label: t('orders:attributes.product.label'), key: 'material.name' },
      { label: t('orders:attributes.product.unit'), key: 'material.unit' },
      { label: t('orders:attributes.product.quantity'), key: 'quantity' },
      { label: t('orders:attributes.product.totalHT'), key: 'price' },
    ],
    [t],
  )

  return (
    <List
      ref={ref}
      {...listProps}
      modes={[
        {
          key: 'table',
          type: ListModeType.table,
          columns: columns,
          collapsible: {
            subColumns: subColumns,
            getSubRows: (order: Order) => order.products,
          },
        },
      ]}
    />
  )
})

export default OrderBuyerList
